import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Lock, Mail, Phone, Shield, ArrowLeft, Loader2, User, ArrowRight, ChevronDown, Globe } from 'lucide-react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { trackSignupStarted, trackSignupCompleted, trackLogin, trackError, setClarityUser, trackOTPSent, trackOTPVerified, trackSignupAbandon, setUserData } from '@/utils/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Phone number formatting utility - Always convert to E.164 format
const formatPhoneToE164 = (input, defaultCountryCode = '+91') => {
  if (!input) return '';
  
  // Remove all non-digit characters except leading +
  let cleaned = input.replace(/[^\d+]/g, '');
  
  // Extract just digits
  const digits = cleaned.replace(/\D/g, '');
  
  // If already in E.164 format (starts with +)
  if (cleaned.startsWith('+') && digits.length >= 10) {
    return cleaned;
  }
  
  // If 10 digits, add default country code
  if (digits.length === 10) {
    return `${defaultCountryCode}${digits}`;
  }
  
  // If 11 digits starting with country code without + (e.g., 919876543210)
  if (digits.length === 12 && digits.startsWith('91')) {
    return `+${digits}`;
  }
  
  // If 11 digits starting with 1 (US format)
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }
  
  // If more than 10 digits, assume country code is included
  if (digits.length > 10) {
    return `+${digits}`;
  }
  
  // Less than 10 digits - return with default country code anyway (will fail validation)
  return `${defaultCountryCode}${digits}`;
};

// Validate phone number - must be exactly 10 digits (without country code)
const validatePhoneDigits = (digits) => {
  const cleaned = digits.replace(/\D/g, '');
  return cleaned.length === 10;
};

// Popular country codes
const COUNTRY_CODES = [
  { code: '+1', country: 'US', name: 'United States', flag: '🇺🇸' },
  { code: '+91', country: 'IN', name: 'India', flag: '🇮🇳' },
  { code: '+44', country: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: '+61', country: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: '+971', country: 'AE', name: 'UAE', flag: '🇦🇪' },
  { code: '+65', country: 'SG', name: 'Singapore', flag: '🇸🇬' },
  { code: '+49', country: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: '+33', country: 'FR', name: 'France', flag: '🇫🇷' },
  { code: '+81', country: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: '+86', country: 'CN', name: 'China', flag: '🇨🇳' },
  { code: '+55', country: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { code: '+52', country: 'MX', name: 'Mexico', flag: '🇲🇽' },
  { code: '+7', country: 'RU', name: 'Russia', flag: '🇷🇺' },
  { code: '+82', country: 'KR', name: 'South Korea', flag: '🇰🇷' },
  { code: '+39', country: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: '+34', country: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: '+31', country: 'NL', name: 'Netherlands', flag: '🇳🇱' },
  { code: '+46', country: 'SE', name: 'Sweden', flag: '🇸🇪' },
  { code: '+41', country: 'CH', name: 'Switzerland', flag: '🇨🇭' },
  { code: '+63', country: 'PH', name: 'Philippines', flag: '🇵🇭' },
  { code: '+92', country: 'PK', name: 'Pakistan', flag: '🇵🇰' },
  { code: '+880', country: 'BD', name: 'Bangladesh', flag: '🇧🇩' },
  { code: '+234', country: 'NG', name: 'Nigeria', flag: '🇳🇬' },
  { code: '+27', country: 'ZA', name: 'South Africa', flag: '🇿🇦' },
  { code: '+62', country: 'ID', name: 'Indonesia', flag: '🇮🇩' },
];

export default function Login() {
  const [mode, setMode] = useState('signup'); // 'signup', 'login', or 'forgot-password'
  const [method, setMethod] = useState('email'); // 'email', 'phone', or 'username'
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [detectedCountry, setDetectedCountry] = useState('IN');
  const [smartInput, setSmartInput] = useState('');
  const [detectedType, setDetectedType] = useState(null); // 'email', 'phone', 'username'
  const [selectedCountryCode, setSelectedCountryCode] = useState('+91'); // Default to India
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [phoneDigits, setPhoneDigits] = useState(''); // Just the digits without country code
  // Forgot Password state
  const [forgotPasswordUserId, setForgotPasswordUserId] = useState(null);
  const [forgotPasswordMethod, setForgotPasswordMethod] = useState(null);
  const [forgotPasswordMaskedContact, setForgotPasswordMaskedContact] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteRef = searchParams.get('ref');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      if (inviteRef) {
        handleInviteRedirect(token);
      } else {
        navigate('/dashboard');
      }
    }
    
    // Auto-detect country from timezone and set default country code
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const countryMap = {
      'Asia/Kolkata': { country: 'IN', code: '+91' },
      'Asia/Calcutta': { country: 'IN', code: '+91' },
      'America/New_York': { country: 'US', code: '+1' },
      'America/Los_Angeles': { country: 'US', code: '+1' },
      'America/Chicago': { country: 'US', code: '+1' },
      'Europe/London': { country: 'GB', code: '+44' },
      'Europe/Paris': { country: 'FR', code: '+33' },
      'Europe/Berlin': { country: 'DE', code: '+49' },
      'Asia/Dubai': { country: 'AE', code: '+971' },
      'Asia/Singapore': { country: 'SG', code: '+65' },
      'Australia/Sydney': { country: 'AU', code: '+61' },
    };
    if (countryMap[timezone]) {
      setDetectedCountry(countryMap[timezone].country);
      setSelectedCountryCode(countryMap[timezone].code);
    }
  }, [navigate, inviteRef]);

  // Smart input detection for login mode
  useEffect(() => {
    if (mode !== 'login' || !smartInput) {
      setDetectedType(null);
      return;
    }

    const input = smartInput.trim();
    
    // Check if it's an email
    if (input.includes('@') && input.includes('.')) {
      setDetectedType('email');
      setEmail(input);
    }
    // Check if it starts with + (international format)
    else if (input.startsWith('+') && input.length > 5) {
      setDetectedType('phone');
      // Extract digits after country code
      const digits = input.replace(/\D/g, '');
      // For +91, the digits will be 91XXXXXXXXXX, so take last 10
      const last10 = digits.slice(-10);
      setPhoneDigits(last10);
      // Always format to E.164
      setPhone(formatPhoneToE164(input, selectedCountryCode));
    }
    // Check if it's just digits (10 digits = phone number)
    else if (/^\d{10}$/.test(input.replace(/\D/g, ''))) {
      setDetectedType('phone');
      const digits = input.replace(/\D/g, '');
      setPhoneDigits(digits);
      // Format with selected country code
      setPhone(formatPhoneToE164(digits, selectedCountryCode));
    }
    // Check if it's digits with country code without + (e.g., 919876543210)
    else if (/^\d{11,15}$/.test(input.replace(/\D/g, ''))) {
      setDetectedType('phone');
      const digits = input.replace(/\D/g, '');
      const last10 = digits.slice(-10);
      setPhoneDigits(last10);
      setPhone(formatPhoneToE164(input, selectedCountryCode));
    }
    // Otherwise treat as username
    else if (input.length >= 3 && !input.includes('@')) {
      setDetectedType('username');
      setUsername(input);
    } else {
      setDetectedType(null);
    }
  }, [smartInput, mode, selectedCountryCode]);

  const handleInviteRedirect = async (token) => {
    try {
      const response = await axios.post(
        `${API}/invite/${inviteRef}/accept`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate(`/chat/${response.data.conversation_id}`);
    } catch (error) {
      console.error('Invite join failed:', error);
      navigate('/dashboard');
    }
  };

  const sendOTP = async () => {
    // For phone, validate 10 digits
    if (method === 'phone') {
      if (!validatePhoneDigits(phoneDigits)) {
        toast.error('Please enter a valid 10-digit mobile number');
        return;
      }
    }
    
    const identifier = method === 'email' ? email : phone;
    if (!identifier || identifier.trim() === '') {
      if (method === 'email') {
        toast.error('Please enter your email address in the field above');
      } else {
        toast.error('Please enter your phone number in the field above');
      }
      return;
    }
    
    // Validate email format
    if (method === 'email' && !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      // Format phone to E.164 before sending
      const formattedPhone = method === 'phone' ? formatPhoneToE164(phoneDigits, selectedCountryCode) : null;
      
      const payload = method === 'email'
        ? { email, method: 'email', mode }
        : { phone: formattedPhone, method: 'sms', mode };
      
      console.log('[sendOTP] Sending payload:', payload);
      
      await axios.post(`${API}/auth/send-otp`, payload);
      
      // Track OTP sent
      trackOTPSent(method);
      if (mode === 'signup') {
        trackSignupStarted(method);
      }
      
      // Update the phone state with formatted version for later use
      if (method === 'phone') {
        setPhone(formattedPhone);
      }
      
      // Show different success message for email vs phone
      if (method === 'email') {
        toast.success(
          <div>
            <p>Verification code sent to your email!</p>
            <p className="text-xs mt-1 opacity-80">Don't see it? Check your spam/junk folder.</p>
          </div>,
          { duration: 5000 }
        );
      } else {
        toast.success('Verification code sent to your phone!');
      }
      setStep(2);
    } catch (error) {
      const errorDetail = error.response?.data?.detail || 'Failed to send verification code';
      
      // Check if it's a "user already exists" error (409 status)
      if (error.response?.status === 409) {
        toast.error(errorDetail, { duration: 6000 });
      } else {
        toast.error(errorDetail);
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }

    setLoading(true);
    try {
      const payload = method === 'email'
        ? { email, code: otp }
        : { phone, code: otp };
      
      const response = await axios.post(`${API}/auth/verify-otp`, payload);
      localStorage.setItem('token', response.data.token);
      
      // Set Clarity user ID for session tracking
      if (response.data.user_id) {
        localStorage.setItem('userId', response.data.user_id);
        setClarityUser(response.data.user_id);
      }
      
      toast.success('Login successful!');
      
      // Track signup/login completion
      if (mode === 'signup') {
        trackSignupCompleted(method);
      } else {
        trackLogin(method);
      }
      
      // Always check if profile needs completion first
      if (response.data.needs_profile) {
        if (inviteRef) {
          localStorage.setItem('pendingInviteRef', inviteRef);
        }
        navigate('/complete-profile');
      } else if (inviteRef) {
        handleInviteRedirect(response.data.token);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      trackError('otp_verification', error.response?.data?.detail || 'Invalid verification code');
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const loginWithPassword = async () => {
    if (!username || !password) {
      toast.error('Please enter username and password');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login-password`, {
        username,
        password
      });
      localStorage.setItem('token', response.data.token);
      
      // Set Clarity user ID for session tracking
      if (response.data.user_id) {
        localStorage.setItem('userId', response.data.user_id);
        setClarityUser(response.data.user_id);
      }
      
      toast.success('Welcome back!');
      
      // Track login
      trackLogin('password');
      
      if (inviteRef) {
        handleInviteRedirect(response.data.token);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      trackError('login', error.response?.data?.detail || 'Invalid credentials');
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleSmartLogin = async () => {
    if (!detectedType) {
      toast.error('Please enter a valid email, phone, or username');
      return;
    }

    setLoading(true);
    try {
      // Format phone to E.164 for validation
      let identifierToValidate = smartInput.trim();
      if (detectedType === 'phone') {
        identifierToValidate = formatPhoneToE164(smartInput.trim(), selectedCountryCode);
      }
      
      // First validate if user exists
      const validateResponse = await axios.post(`${API}/auth/validate-login-identifier`, {
        identifier: identifierToValidate
      });
      
      const { login_type, username: validatedUsername } = validateResponse.data;
      
      if (detectedType === 'username') {
        if (login_type === 'no_password') {
          toast.error('No password set for this account. Please use email or phone to login.');
          return;
        }
        // User exists and has password - proceed to password step
        setUsername(validatedUsername || smartInput.trim());
        setStep(2);
      } else {
        // Email or phone - send OTP
        setMethod(detectedType);
        
        // Format phone properly before sending
        const formattedPhone = detectedType === 'phone' 
          ? formatPhoneToE164(phoneDigits || smartInput.trim(), selectedCountryCode)
          : null;
        
        const payload = detectedType === 'email'
          ? { email, method: 'email' }
          : { phone: formattedPhone, method: 'sms' };
        
        console.log('[handleSmartLogin] Sending OTP payload:', payload);
        
        await axios.post(`${API}/auth/send-otp`, payload);
        
        // Update phone state with formatted version
        if (detectedType === 'phone') {
          setPhone(formattedPhone);
        }
        
        toast.success(`Verification code sent!`);
        setStep(2);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Something went wrong';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setOtp('');
    setPassword('');
    setSmartInput('');
    setDetectedType(null);
    // Reset forgot password state
    setForgotPasswordUserId(null);
    setForgotPasswordMethod(null);
    setForgotPasswordMaskedContact('');
    setResetToken('');
    setNewPassword('');
    setConfirmPassword('');
  };

  // ============================================
  // FORGOT PASSWORD HANDLERS
  // ============================================
  
  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleForgotPasswordRequest = async () => {
    if (!smartInput.trim()) {
      toast.error('Please enter your username, email, or phone number');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password/request`, {
        identifier: smartInput.trim()
      });

      if (response.data.status === 'success') {
        setForgotPasswordUserId(response.data.user_id);
        setForgotPasswordMethod(response.data.method);
        setForgotPasswordMaskedContact(response.data.masked_contact);
        setStep(2);
        setResendCooldown(60);
        toast.success(`Verification code sent to ${response.data.masked_contact}`);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to send verification code';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordVerify = async () => {
    if (!otp || otp.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password/verify`, {
        user_id: forgotPasswordUserId,
        code: otp
      });

      if (response.data.status === 'success') {
        setResetToken(response.data.reset_token);
        setStep(3);
        toast.success('Code verified! Set your new password.');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Invalid verification code';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordReset = async () => {
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password/reset`, {
        reset_token: resetToken,
        new_password: newPassword,
        confirm_password: confirmPassword
      });

      if (response.data.status === 'success') {
        toast.success('Password reset successful! Please login.');
        // Reset form and go to login
        resetForm();
        setMode('login');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to reset password';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleResendForgotPasswordOTP = async () => {
    if (resendCooldown > 0) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password/request`, {
        identifier: smartInput.trim()
      });

      if (response.data.status === 'success') {
        setForgotPasswordUserId(response.data.user_id);
        setResendCooldown(60);
        toast.success('New verification code sent!');
      }
    } catch (error) {
      toast.error('Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // RENDER FORGOT PASSWORD STEPS
  // ============================================
  
  const renderForgotPasswordStep1 = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <p className="text-sm text-gray-400">
          Enter your username, email, or phone number to receive a verification code.
        </p>
      </div>
      
      <div className="relative">
        <Input
          type="text"
          placeholder="Username, email, or phone"
          value={smartInput}
          onChange={(e) => setSmartInput(e.target.value)}
          className="w-full h-12 pl-10 bg-gray-900/50 border-white/10 text-white placeholder:text-gray-500 rounded-xl"
          onKeyPress={(e) => e.key === 'Enter' && handleForgotPasswordRequest()}
          data-testid="forgot-password-input"
        />
        <User className="absolute left-3 top-3.5 h-5 w-5 text-gray-500" />
      </div>
      
      <Button
        onClick={handleForgotPasswordRequest}
        disabled={loading || !smartInput.trim()}
        className="w-full h-12 rounded-xl text-white font-medium"
        style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
        data-testid="forgot-password-submit"
      >
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <>
            Send Verification Code
            <ArrowRight className="ml-2 h-4 w-4" />
          </>
        )}
      </Button>
      
      <Button
        variant="ghost"
        onClick={() => {
          resetForm();
          setMode('login');
        }}
        className="w-full text-gray-400 hover:text-white"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Login
      </Button>
    </div>
  );

  const renderForgotPasswordStep2 = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <p className="text-sm text-gray-400">
          Enter the 6-digit code sent to
        </p>
        <p className="text-white font-medium mt-1">
          {forgotPasswordMaskedContact}
        </p>
      </div>
      
      <div className="relative">
        <Input
          type="text"
          placeholder="Enter 6-digit code"
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
          maxLength={6}
          className="w-full h-12 pl-10 bg-gray-900/50 border-white/10 text-white placeholder:text-gray-500 rounded-xl text-center text-xl tracking-widest"
          onKeyPress={(e) => e.key === 'Enter' && handleForgotPasswordVerify()}
          data-testid="forgot-password-otp"
        />
        <Shield className="absolute left-3 top-3.5 h-5 w-5 text-gray-500" />
      </div>
      
      <Button
        onClick={handleForgotPasswordVerify}
        disabled={loading || otp.length !== 6}
        className="w-full h-12 rounded-xl text-white font-medium"
        style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
        data-testid="forgot-password-verify"
      >
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <>
            Verify Code
            <ArrowRight className="ml-2 h-4 w-4" />
          </>
        )}
      </Button>
      
      <div className="flex justify-between items-center">
        <Button
          variant="ghost"
          onClick={() => setStep(1)}
          className="text-gray-400 hover:text-white"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        
        <Button
          variant="ghost"
          onClick={handleResendForgotPasswordOTP}
          disabled={resendCooldown > 0 || loading}
          className="text-gray-400 hover:text-white"
        >
          {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
        </Button>
      </div>
    </div>
  );

  const renderForgotPasswordStep3 = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <p className="text-sm text-gray-400">
          Create a strong password for your account
        </p>
      </div>
      
      <div className="relative">
        <Input
          type="password"
          placeholder="New Password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="w-full h-12 pl-10 bg-gray-900/50 border-white/10 text-white placeholder:text-gray-500 rounded-xl"
          data-testid="forgot-password-new"
        />
        <Lock className="absolute left-3 top-3.5 h-5 w-5 text-gray-500" />
      </div>
      
      <div className="relative">
        <Input
          type="password"
          placeholder="Confirm Password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="w-full h-12 pl-10 bg-gray-900/50 border-white/10 text-white placeholder:text-gray-500 rounded-xl"
          onKeyPress={(e) => e.key === 'Enter' && handleForgotPasswordReset()}
          data-testid="forgot-password-confirm"
        />
        <Lock className="absolute left-3 top-3.5 h-5 w-5 text-gray-500" />
      </div>
      
      {newPassword && (
        <div className="text-xs text-gray-400">
          {newPassword.length < 6 ? (
            <span className="text-red-400">Password must be at least 6 characters</span>
          ) : newPassword !== confirmPassword && confirmPassword ? (
            <span className="text-red-400">Passwords do not match</span>
          ) : newPassword === confirmPassword && confirmPassword ? (
            <span className="text-green-400">Passwords match ✓</span>
          ) : null}
        </div>
      )}
      
      <Button
        onClick={handleForgotPasswordReset}
        disabled={loading || newPassword.length < 6 || newPassword !== confirmPassword}
        className="w-full h-12 rounded-xl text-white font-medium"
        style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
        data-testid="forgot-password-reset"
      >
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <>
            Reset Password
            <ArrowRight className="ml-2 h-4 w-4" />
          </>
        )}
      </Button>
    </div>
  );

  // Signup Mode - Choose method first
  const renderSignupStep1 = () => (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button
          variant={method === 'email' ? 'default' : 'outline'}
          onClick={() => setMethod('email')}
          className={`flex-1 h-12 ${method === 'email' ? 'text-white' : 'text-gray-400 border-white/10 hover:bg-white/5'}`}
          style={method === 'email' ? { background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' } : {}}
          data-testid="email-method-btn"
        >
          <Mail className="h-4 w-4 mr-2" />
          Email
        </Button>
        <Button
          variant={method === 'phone' ? 'default' : 'outline'}
          onClick={() => setMethod('phone')}
          className={`flex-1 h-12 ${method === 'phone' ? 'text-white' : 'text-gray-400 border-white/10 hover:bg-white/5'}`}
          style={method === 'phone' ? { background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' } : {}}
          data-testid="phone-method-btn"
        >
          <Phone className="h-4 w-4 mr-2" />
          Phone
        </Button>
      </div>

      {method === 'email' ? (
        <div className="space-y-2">
          <label className="text-sm font-medium" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>
            Your email address
          </label>
          <Input
            type="email"
            placeholder="name@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12 text-base"
            style={{ fontSize: '16px' }}
            data-testid="email-input"
            autoComplete="email"
            autoFocus
          />
          <p className="text-xs" style={{ color: 'rgba(229, 231, 235, 0.4)' }}>
            We'll send a verification code to this email
          </p>
        </div>
      ) : (
        <div className="relative">
          <Popover open={showCountryPicker} onOpenChange={setShowCountryPicker}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-1 px-2 py-1 rounded-md transition-all hover:bg-white/10 z-10"
                style={{ color: '#E5E7EB' }}
              >
                <span className="text-lg">{COUNTRY_CODES.find(c => c.code === selectedCountryCode)?.flag || '🌍'}</span>
                <span className="text-sm font-medium">{selectedCountryCode}</span>
                <ChevronDown className="h-3 w-3 opacity-50" />
              </button>
            </PopoverTrigger>
            <PopoverContent 
              className="w-64 p-2 max-h-72 overflow-y-auto"
              style={{ background: 'rgba(10,15,44,0.98)', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <div className="space-y-1">
                {COUNTRY_CODES.map((country) => (
                  <button
                    key={country.code}
                    onClick={() => {
                      setSelectedCountryCode(country.code);
                      if (phoneDigits) {
                        setPhone(`${country.code}${phoneDigits}`);
                      }
                      setShowCountryPicker(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${selectedCountryCode === country.code ? 'bg-purple-500/20' : 'hover:bg-white/10'}`}
                  >
                    <span className="text-xl">{country.flag}</span>
                    <span className="text-sm flex-1 text-left" style={{ color: '#E5E7EB' }}>{country.name}</span>
                    <span className="text-sm font-mono" style={{ color: 'rgba(229,231,235,0.6)' }}>{country.code}</span>
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          
          <Input
            type="tel"
            placeholder="Phone number"
            value={phoneDigits}
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, '');
              setPhoneDigits(digits);
              setPhone(`${selectedCountryCode}${digits}`);
            }}
            className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12 pl-28"
            data-testid="phone-input"
          />
        </div>
      )}

      <Button 
        onClick={sendOTP}
        className="w-full h-12 text-white font-semibold"
        style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
        disabled={loading}
        data-testid="send-otp-button"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
          <>
            Continue
            <ArrowRight className="h-4 w-4 ml-2" />
          </>
        )}
      </Button>
    </div>
  );

  // Signup Mode - OTP verification
  const renderSignupStep2 = () => (
    <div className="space-y-4">
      <button 
        onClick={resetForm}
        className="flex items-center gap-2 text-sm transition-colors hover:text-white"
        style={{ color: 'rgba(229, 231, 235, 0.5)' }}
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>

      <div className="text-center">
        <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>
          Enter the 6-digit code sent to
        </p>
        <p className="font-medium truncate" style={{ color: '#E5E7EB' }}>
          {method === 'email' ? email : phone}
        </p>
        {method === 'email' && (
          <p className="text-xs mt-2 px-3 py-1.5 rounded-md inline-block" style={{ color: '#22D3EE', background: 'rgba(34, 211, 238, 0.1)' }}>
            Can't find it? Check your spam/junk folder
          </p>
        )}
      </div>

      <Input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        placeholder="000000"
        value={otp}
        onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
        maxLength={6}
        className="bg-white/5 border-white/10 text-white text-center text-2xl tracking-[0.5em] placeholder:text-gray-600 h-14"
        data-testid="otp-input"
        autoComplete="one-time-code"
      />

      <Button 
        onClick={verifyOTP}
        className="w-full h-12 text-white font-semibold"
        style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
        disabled={loading || otp.length !== 6}
        data-testid="verify-otp-button"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify & Continue'}
      </Button>

      <button 
        onClick={sendOTP}
        className="w-full text-sm text-center transition-colors hover:text-white"
        style={{ color: 'rgba(229, 231, 235, 0.5)' }}
        disabled={loading}
      >
        Didn't receive code? Resend
      </button>
    </div>
  );

  // Login Mode - Smart input
  const renderLoginStep1 = () => {
    const selectedCountry = COUNTRY_CODES.find(c => c.code === selectedCountryCode) || COUNTRY_CODES[1];
    
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          {/* Main input with country code picker for phone */}
          <div className="relative">
            {detectedType === 'phone' && (
              <Popover open={showCountryPicker} onOpenChange={setShowCountryPicker}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-1 px-2 py-1 rounded-md transition-all hover:bg-white/10 z-10"
                    style={{ color: '#E5E7EB' }}
                  >
                    <span className="text-lg">{selectedCountry.flag}</span>
                    <span className="text-sm font-medium">{selectedCountryCode}</span>
                    <ChevronDown className="h-3 w-3 opacity-50" />
                  </button>
                </PopoverTrigger>
                <PopoverContent 
                  className="w-64 p-2 max-h-72 overflow-y-auto"
                  style={{ background: 'rgba(10,15,44,0.98)', border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  <div className="space-y-1">
                    {COUNTRY_CODES.map((country) => (
                      <button
                        key={country.code}
                        onClick={() => {
                          setSelectedCountryCode(country.code);
                          setPhone(`${country.code}${phoneDigits}`);
                          setShowCountryPicker(false);
                        }}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${selectedCountryCode === country.code ? 'bg-purple-500/20' : 'hover:bg-white/10'}`}
                      >
                        <span className="text-xl">{country.flag}</span>
                        <span className="text-sm flex-1 text-left" style={{ color: '#E5E7EB' }}>{country.name}</span>
                        <span className="text-sm font-mono" style={{ color: 'rgba(229,231,235,0.6)' }}>{country.code}</span>
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            )}
            
            <Input
              type="text"
              placeholder="Username, email, or phone number"
              value={smartInput}
              onChange={(e) => setSmartInput(e.target.value)}
              className={`bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12 ${detectedType === 'phone' ? 'pl-28' : ''}`}
              data-testid="smart-login-input"
            />
          </div>
        </div>

        <Button 
          onClick={handleSmartLogin}
          className="w-full h-12 text-white font-semibold"
          style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
          disabled={loading || !detectedType}
          data-testid="smart-login-button"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Continue'}
        </Button>
      </div>
    );
  };

  // Login Mode - Step 2 (OTP or Password)
  const renderLoginStep2 = () => (
    <div className="space-y-4">
      <button 
        onClick={resetForm}
        className="flex items-center gap-2 text-sm transition-colors hover:text-white"
        style={{ color: 'rgba(229, 231, 235, 0.5)' }}
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>

      {detectedType === 'username' ? (
        // Password login
        <>
          <div className="text-center">
            <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>
              Welcome back
            </p>
            <p className="font-medium" style={{ color: '#E5E7EB' }}>@{username}</p>
          </div>

          <Input
            type="password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12"
            data-testid="password-input"
            onKeyPress={(e) => e.key === 'Enter' && password && loginWithPassword()}
          />

          <Button 
            type="button"
            onClick={loginWithPassword}
            className="w-full h-12 text-white font-semibold"
            style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
            disabled={loading || !password}
            data-testid="login-submit-button"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Login'}
          </Button>
          
          {/* Forgot Password Link - Only for password login */}
          <div className="text-center">
            <button
              onClick={() => {
                setMode('forgot-password');
                setStep(1);
                setSmartInput(username); // Pre-fill with username
              }}
              className="text-sm transition-colors hover:underline"
              style={{ color: '#22D3EE' }}
              data-testid="forgot-password-link"
            >
              Forgot your password?
            </button>
          </div>
        </>
      ) : (
        // OTP verification
        <>
          <div className="text-center">
            <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>
              Enter the 6-digit code sent to
            </p>
            <p className="font-medium truncate" style={{ color: '#E5E7EB' }}>
              {detectedType === 'email' ? email : phone}
            </p>
            {detectedType === 'email' && (
              <p className="text-xs mt-2 px-3 py-1.5 rounded-md inline-block" style={{ color: '#22D3EE', background: 'rgba(34, 211, 238, 0.1)' }}>
                Can't find it? Check your spam/junk folder
              </p>
            )}
          </div>

          <Input
            type="text"
            inputMode="numeric"
            placeholder="000000"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
            maxLength={6}
            className="bg-white/5 border-white/10 text-white text-center text-2xl tracking-[0.5em] h-14"
            data-testid="otp-input"
          />

          <Button 
            onClick={verifyOTP}
            className="w-full h-12 text-white font-semibold"
            style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
            disabled={loading || otp.length !== 6}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify & Login'}
          </Button>
        </>
      )}
    </div>
  );

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg, #0A0F2C 0%, #020617 100%)' }}
    >
      {/* Glowing orbs */}
      <div 
        className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full opacity-20 blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, #7C3AED 0%, transparent 70%)' }}
      />
      <div 
        className="absolute bottom-1/4 right-1/4 w-48 h-48 rounded-full opacity-15 blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, #22D3EE 0%, transparent 70%)' }}
      />
      
      <Card 
        className="w-full max-w-md border-0 relative z-10"
        style={{ 
          background: 'rgba(255, 255, 255, 0.03)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.08)'
        }}
        data-testid="login-card"
      >
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div 
              className="p-3 rounded-full"
              style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
            >
              <Shield className="h-8 w-8 text-white" />
            </div>
          </div>
          <CardTitle 
            className="text-2xl font-bold"
            style={{ color: '#E5E7EB' }}
          >
            {mode === 'signup' ? 'Enter Privluxe' : mode === 'forgot-password' ? 'Reset Password' : 'Welcome Back'}
          </CardTitle>
          <CardDescription style={{ color: 'rgba(229, 231, 235, 0.5)' }}>
            {mode === 'signup' 
              ? 'Your private, secure messaging network'
              : mode === 'forgot-password'
              ? 'Recover access to your account'
              : 'Login to your account'}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Mode Toggle - Hidden in forgot password mode */}
          {mode !== 'forgot-password' && (
            <div className="flex gap-2 p-1.5 rounded-xl" style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <button
                type="button"
                onClick={() => { setMode('signup'); resetForm(); trackSignupStarted(); }}
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all ${
                  mode === 'signup' ? 'text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
                style={mode === 'signup' ? { background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' } : {}}
                data-testid="signup-tab"
              >
                Sign Up
              </button>
              <button
                type="button"
                onClick={() => { setMode('login'); resetForm(); }}
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all ${
                  mode === 'login' ? 'text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
                style={mode === 'login' ? { background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' } : {}}
                data-testid="login-tab"
              >
                Login
              </button>
            </div>
          )}

          {/* Signup Flow */}
          {mode === 'signup' && step === 1 && renderSignupStep1()}
          {mode === 'signup' && step === 2 && renderSignupStep2()}

          {/* Login Flow */}
          {mode === 'login' && step === 1 && renderLoginStep1()}
          {mode === 'login' && step === 2 && renderLoginStep2()}

          {/* Forgot Password Flow */}
          {mode === 'forgot-password' && step === 1 && renderForgotPasswordStep1()}
          {mode === 'forgot-password' && step === 2 && renderForgotPasswordStep2()}
          {mode === 'forgot-password' && step === 3 && renderForgotPasswordStep3()}

          {/* Security badge */}
          <div className="flex items-center justify-center gap-2 text-xs pt-2" style={{ color: 'rgba(229, 231, 235, 0.3)' }}>
            <Lock className="h-3 w-3" />
            End-to-end encrypted
          </div>
        </CardContent>
      </Card>

      {/* Phone input styles */}
      <style>{`
        .phone-input-dark .PhoneInputInput {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 0.5rem;
          padding: 0.75rem 1rem;
          color: white;
          font-size: 16px;
          height: 48px;
        }
        .phone-input-dark .PhoneInputInput:focus {
          border-color: #7C3AED;
          outline: none;
        }
        .phone-input-dark .PhoneInputInput::placeholder {
          color: rgba(156, 163, 175, 0.5);
        }
        .phone-input-dark .PhoneInputCountrySelect {
          background: rgba(255, 255, 255, 0.05);
          color: white;
        }
        .phone-input-dark .PhoneInputCountryIcon {
          width: 24px;
          height: 18px;
        }
      `}</style>
    </div>
  );
}
